import { FAST_HOURS_PER_MONTH_DEFAULT } from '@src/components/sco/Advisors/data/fastAgreement'
import { AdvisorsPaths } from '@src/components/sco/Advisors/paths'
import {
  AGREEMENT_CLIFF_MONTHS_DEFAULT,
  AGREEMENT_TERM_MONTHS_DEFAULT,
  AGREEMENT_VESTING_MONTHS_DEFAULT,
} from '@src/components/sco/Advisors/utils/agreementKeys'
import { defaultAgreementStartDate } from '@src/components/sco/Advisors/utils/fastAgreementForm'
import {
  ADVISOR_ENGAGEMENT_SOURCE_AI,
  ADVISOR_ENGAGEMENT_SOURCE_TEAM,
} from '@src/constants/advisor-engagement-sources'
import { ScoAdvisorEngagement, UpsertAdvisorAgreementInput } from '@src/graphql-types'

/** Founder-owned recruited seats can carry a FAST. Wil / team seats cannot. */
export const canDraftFastForEngagement = (
  engagement?: Pick<
    ScoAdvisorEngagement,
    'advisorEngagementId' | 'isAI' | 'sourceKey' | 'currentUserCanEdit'
  > | null
): boolean => {
  if (!engagement?.advisorEngagementId) {
    return false
  }
  if (engagement.currentUserCanEdit === false) {
    return false
  }
  if (engagement.isAI || engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_AI) {
    return false
  }
  if (engagement.sourceKey === ADVISOR_ENGAGEMENT_SOURCE_TEAM) {
    return false
  }
  return true
}

export const advisorPartyFromEngagement = (engagement: ScoAdvisorEngagement) => {
  const advisorUserId = engagement.advisorUserId || engagement.advisorUser?.userId || null
  const advisorName =
    engagement.advisorName || engagement.advisorUser?.fullName || engagement.advisorUser?.displayName || null
  const advisorEmail = engagement.advisorEmail || engagement.advisorUser?.email || null
  const advisorTitle = engagement.category?.title || null
  const companyId = engagement.companyId || engagement.company?.companyId || null

  return { advisorUserId, advisorName, advisorEmail, advisorTitle, companyId }
}

/** Ad-hoc FAST draft with no seat. Advisor party is filled in on the agreement form. */
export const blankAgreementInput = (): UpsertAdvisorAgreementInput => ({
  vestingMonths: AGREEMENT_VESTING_MONTHS_DEFAULT,
  cliffMonths: AGREEMENT_CLIFF_MONTHS_DEFAULT,
  termMonths: AGREEMENT_TERM_MONTHS_DEFAULT,
  hoursPerMonth: FAST_HOURS_PER_MONTH_DEFAULT,
  startDate: defaultAgreementStartDate(),
  fastTrackApplied: true,
})

/** Prefill for `upsertAdvisorAgreement` — seat FK + advisor party + company. */
export const upsertInputFromEngagement = (engagement: ScoAdvisorEngagement): UpsertAdvisorAgreementInput => {
  const party = advisorPartyFromEngagement(engagement)
  return {
    advisorEngagementId: engagement.advisorEngagementId,
    advisorUserId: party.advisorUserId,
    advisorName: party.advisorName,
    advisorEmail: party.advisorEmail,
    advisorTitle: party.advisorTitle,
    companyId: party.companyId,
    vestingMonths: AGREEMENT_VESTING_MONTHS_DEFAULT,
    cliffMonths: AGREEMENT_CLIFF_MONTHS_DEFAULT,
    termMonths: AGREEMENT_TERM_MONTHS_DEFAULT,
    hoursPerMonth: FAST_HOURS_PER_MONTH_DEFAULT,
    startDate: defaultAgreementStartDate(),
    fastTrackApplied: true,
  }
}

export const fastAgreementHref = (paths: Pick<AdvisorsPaths, 'agreementEdit'>, agreementId: string) =>
  paths.agreementEdit(agreementId)

export const currentAgreementId = (engagement?: ScoAdvisorEngagement | null) =>
  engagement?.currentAgreement?.advisorAgreementId || null
